export type ParsedProfileAddress = {
  addressLine: string
  city: string
  postalCode: string
  province: string
  country: string
  phone: string
}

const COUNTRY_ALIASES: Record<string, string> = {
  IT: 'IT',
  ITALIA: 'IT',
  ITALY: 'IT',
}

const splitAddressParts = (value: string): string[] =>
  value
    .split(/\n|,/g)
    .map((part) => part.trim())
    .filter(Boolean)

const resolveCountryCode = (value: string | undefined, fallback: string): string => {
  const normalized = value?.trim().toUpperCase() ?? ''
  if (COUNTRY_ALIASES[normalized]) return COUNTRY_ALIASES[normalized]
  if (/^[A-Z]{2}$/.test(normalized)) return normalized

  const normalizedFallback = fallback.trim().toUpperCase()
  return COUNTRY_ALIASES[normalizedFallback] ?? (/^[A-Z]{2}$/.test(normalizedFallback) ? normalizedFallback : 'IT')
}

const parseCityAndProvince = (value: string) => {
  const provinceMatch = value.match(/^(.*?)\s+\(([^()]{1,30})\)\s*$/)
  return {
    city: (provinceMatch?.[1] ?? value).trim(),
    province: provinceMatch?.[2]?.trim().toUpperCase() ?? '',
  }
}

const parseLocationPart = (value: string) => {
  const postalMatch = value.match(/^(\d{4,6})\s+(.+)$/)
  if (!postalMatch) return null

  let location = postalMatch[2].trim()
  let inlineCountry = ''
  const countryMatch = location.match(/\s+(IT|ITALIA|ITALY)\s*$/i)
  if (countryMatch) {
    inlineCountry = countryMatch[1]
    location = location.slice(0, countryMatch.index).trim()
  }

  const { city, province } = parseCityAndProvince(location)
  if (!city) return null

  return {
    postalCode: postalMatch[1],
    city,
    province,
    inlineCountry,
  }
}

const looksLikePhone = (value: string): boolean =>
  /^\+?[\d\s()./-]{6,}$/.test(value.trim())

export const parseProfileAddressValue = ({
  value,
  countryFallback = 'IT',
  phoneFallback = '',
}: {
  value: string
  countryFallback?: string
  phoneFallback?: string
}): ParsedProfileAddress | null => {
  const trimmed = value.trim()
  if (!trimmed) return null

  const parts = splitAddressParts(trimmed)
  const locationIndex = parts.findIndex((part) => /^\d{4,6}\s+/.test(part))
  const parsedLocation = locationIndex >= 0 ? parseLocationPart(parts[locationIndex]) : null

  if (parsedLocation) {
    const followingParts = parts.slice(locationIndex + 1)
    const countryPart = followingParts.find((part) => {
      const normalized = part.trim().toUpperCase()
      return Boolean(COUNTRY_ALIASES[normalized]) || /^[A-Z]{2}$/.test(normalized)
    })
    const phonePart = followingParts.find(looksLikePhone)

    return {
      addressLine: (locationIndex > 0 ? parts[locationIndex - 1] : parts[0]).trim(),
      city: parsedLocation.city,
      postalCode: parsedLocation.postalCode,
      province: parsedLocation.province,
      country: resolveCountryCode(parsedLocation.inlineCountry || countryPart, countryFallback),
      phone: (phonePart ?? phoneFallback).trim(),
    }
  }

  const addressLine = parts[0] ?? trimmed
  const cityPart = parts[1] ?? ''
  const { city, province } = parseCityAndProvince(cityPart)
  const countryPart = parts.slice(2).find((part) => !looksLikePhone(part))
  const phonePart = parts.slice(2).find(looksLikePhone)

  return {
    addressLine,
    city,
    postalCode: '',
    province,
    country: resolveCountryCode(countryPart, countryFallback),
    phone: (phonePart ?? phoneFallback).trim(),
  }
}
